import React, { useState } from "react";
import {
  DownloadIcon,
  ChartCandlestick,
  MessageSquareText,
  Wrench,
  BadgeCheck,
  ChartNoAxesCombined,
  Lightbulb,
  ShieldAlert,
} from "lucide-react";
import { downloadAuditReport } from "../../../api/download/downloadapi";

import "./AuditReport.css";

export type MetricsToTrack = {
  why: string;
  metric: string;
  target: string;
};

export type ActionPlan = {
  next30Days: string[];
  next60Days: string[];
  next90Days: string[];
};

export type BackendRecommendation = {
  action: string;
  impact: string;
  priority: string;
};

export type Finding = {
  title: string;
  impact: string;
  evidence: string;
};

export type DeepDiveItem = {
  key: string;
  icon: string;
  title: string;
  subtitle: string;
  score: number;
  findings: Finding[];
  assessment: string;
  recommendations: BackendRecommendation[];
};

export type QuickWin = {
  no: string;
  title: string;
  description: string;
};

export type IndustryTrends = {
  threats: string[];
  opportunities: string[];
  trends: string[];
};

export type Snapshot = {
  image: string;
  title: string;
  description: string;
  metaDescription: string;
};

export type InfographicMetric = {
  label: string;
  value: number;
  unit: string;
  sentiment: "positive" | "negative" | "neutral";
};

export type Benchmark = {
  label: string;
  value: string;
  ideal: string;
  status: "good" | "warning" | "critical";
};

export type HealthCheck = {
  label: string;
  detail: string;
  passed: boolean;
};

export type Infographics = {
  keyMetrics: InfographicMetric[];
  benchmarks: Benchmark[];
  healthChecks: HealthCheck[];
  categoryScores: { category: string; score: number }[];
  findingsSummary: { strengths: number; weaknesses: number };
  recommendationPriority: { low: number; medium: number; high: number };
};

export type AuditReportDTO = {
  brandName: string;
  email: string; 
  auditType: "web" | "social"; 
  url: string;
  date: string;

  tagline: string;
  platformLabel: string;

  score: number;
  scoreLabel: "Excellent" | "Good" | "Needs Improvement";
  scoreCaption: string;

  paradox: {
    title: string;
    paragraphs: string[];
    tags: string[];
  };

  infographics: Infographics | null;

  deepDive: DeepDiveItem[];

  industryTrends: IndustryTrends;

  quickWins: {
    title: string;
    subtitle: string;
    items: QuickWin[];
  };

  actionPlan: ActionPlan;

  competitorLens: string;

  metricsToTrack: MetricsToTrack[];

  painPoints: {
    title: string;
    body: string;
    userSubmitted: string;
  };

  snapshot: Snapshot | null;

  cta: {
    title: string;
    subtitle: string;
    buttonText: string;
  };
};
const deepDiveIcons: Record<string, React.ReactNode> = {
  seo: (
    <ChartCandlestick size={22} strokeWidth={2.2} color="#001147" />
  ),

  content: (
    <MessageSquareText size={22} strokeWidth={2.2} color="#001147" />
  ),

  technical: (
    <Wrench size={22} strokeWidth={2.2} color="#001147" />
  ),

  conversion: (
    <BadgeCheck size={22} strokeWidth={2.2} color="#001147" />
  ),

  profile: (
    <BadgeCheck size={22} strokeWidth={2.2} color="#001147" />
  ),

  engagement: (
    <ChartCandlestick size={22} strokeWidth={2.2} color="#001147" />
  ),

  brand: (
    <Wrench size={22} strokeWidth={2.2} color="#001147" />
  ),

  default: (
    <ChartCandlestick size={22} strokeWidth={2.2} color="#001147" />
  ),
};

const IndustryIcons: Record<string, React.ReactNode> = {
  Threats: <ShieldAlert size={22} strokeWidth={2.2} color="#001147" />,
  Opportunities: <Lightbulb size={22} strokeWidth={2.2} color="#001147" />,
  Trends: <ChartNoAxesCombined size={22} strokeWidth={2.2} color="#001147" />,
};

const GAUGE_RADIUS = 93;
const GAUGE_CIRCUMFERENCE = 2 * Math.PI * GAUGE_RADIUS;

const GAUGE_SEGMENTS = 4;
const GAUGE_SEG_LEN = GAUGE_CIRCUMFERENCE / GAUGE_SEGMENTS;
const GAUGE_DASH = `${GAUGE_SEG_LEN * 0.32} ${GAUGE_SEG_LEN * 0.68}`;


const AuditReport: React.FC<{ report: AuditReportDTO }> = ({ report }) => {
  const [isDownloading, setIsDownloading] = useState(false);

const [downloadMessage, setDownloadMessage] = useState("");
const handleDownloadReport = async () => {
  try {
    setIsDownloading(true);
    setDownloadMessage("");

    const type = report.auditType;
    const email = localStorage.getItem("auditEmail") || "";

    const response = await downloadAuditReport({
      type,
      email,
    });

    localStorage.removeItem("auditEmail");

    setDownloadMessage(
      response.message || "Report has been successfully sent to your email."
    );
  } catch (error: any) {
    console.error(error);

    let message = "Something went wrong. Please try again.";

    if (error.response) {
      switch (error.response.status) {
        case 400:
          message =
            error.response.data?.message ||
            "Invalid request. Please check your details.";
          break;

        case 401:
          message = "You are not authorized. Please log in again.";
          break;

        case 403:
          message =
            error.response.data?.message ||
            "You don't have permission to perform this action.";
          break;

        case 404:
          message =
            error.response.data?.message ||
            "No audit report found for this email.";
          break;

        case 409:
          message =
            error.response.data?.message ||
            "A report already exists for this request.";
          break;

        case 429:
          message =
            error.response.data?.message ||
            "API usage limit reached. Please try again later.";
          break;

        case 500:
          message =
            error.response.data?.message ||
            "Server error. Please try again after some time.";
          break;

        default:
          message =
            error.response.data?.message ||
            "Something went wrong. Please try again.";
      }
    } else if (error.request) {
      message =
        "Unable to reach the server. Please check your internet connection.";
    }

    setDownloadMessage(message);
  } finally {
    setIsDownloading(false);
  }
};

  console.log(report);

  const info = report?.infographics;

  const scoreBarColor = (s: number) =>
    s >= 70 ? "#22c55e" : s >= 40 ? "#3257d6" : "#ef4444";

  const formatMetricValue = (value: number, unit: string) => {
    const num =
      typeof value === "number" ? value.toLocaleString("en-US") : value;
    return unit ? `${num} ${unit}` : `${num}`;
  };

  // Donut geometry for "Recommendations by Priority"
  const DONUT_R = 54;
  const DONUT_C = 2 * Math.PI * DONUT_R;

  const prioritySegments = info
    ? [
        { label: "High", value: info.recommendationPriority.high, color: "#ef4444" },
        { label: "Medium", value: info.recommendationPriority.medium, color: "#f59e0b" },
        { label: "Low", value: info.recommendationPriority.low, color: "#22c55e" },
      ]
    : [];

  const priorityTotal = prioritySegments.reduce((sum, s) => sum + s.value, 0);

  const findingsTotal = info
    ? info.findingsSummary.strengths + info.findingsSummary.weaknesses
    : 0;

  return (

    <div className="audit-report">
      <div className="audit-report__inner">

        {/* ================= HEADER ================= */}
        <header className="audit-report__header">
  <h1 className="audit-report__brand-title">{report?.url}</h1>
  <p className="audit-report__brand-tagline">
    We analyze your digital presence to identify growth areas and bottlenecks.
  </p>
  <p className="audit-report__analysis-line">
    {report?.auditType === "web" ? "Web" : "Social"} Analysis •{" "}
    {report?.url}
  </p>

  <div className="audit-report__gauge">
    <svg className="audit-report__gauge-svg" viewBox="0 0 180 180">
      <defs>
        <linearGradient id="auditGaugeGradient" x1="0%" y1="50%" x2="100%" y2="100%">
          <stop offset="50%" stopColor="#1757ff" />
          <stop offset="100%" stopColor="#69b0ff" />
        </linearGradient>
      </defs>

      <circle className="audit-report__gauge-outer" cx="90" cy="90" r="100" />
      <circle className="audit-report__gauge-track" cx="90" cy="90" r={GAUGE_RADIUS} />
      <circle
        className="audit-report__gauge-progress"
        cx="90"
        cy="90"
        r={GAUGE_RADIUS}
        strokeDasharray={GAUGE_DASH}
      />
      <circle className="audit-report__gauge-disc" cx="90" cy="90" r="85.5" />
    </svg>

    <div className="audit-report__gauge-center">
      <span className="audit-report__gauge-value">
        {report?.score ?? 0}
        <span className="audit-report__gauge-pct">%</span>
      </span>

      <span className="audit-report__gauge-label">
        {report?.scoreLabel ?? "Loading"}
      </span>
    </div>
  </div>

  <h2 className="audit-report__overall-label">Overall Assessment</h2>
  <div className="audit-report__gauge-caption">
    {report?.scoreCaption ?? ""}
  </div>
</header>

        {/* ================= AT A GLANCE (INFOGRAPHICS) ================= */}
        {info && (
          <section className="audit-report__glance">
            <h2 className="audit-report__section-heading">At a Glance</h2>

            {/* Category Scores */}
            {info.categoryScores.length > 0 && (
              <div className="audit-report__glance-card">
                <h3 className="audit-report__glance-title">Category Scores</h3>

                <div className="audit-report__scorebars">
                  {info.categoryScores.map((c, i) => (
                    <div key={i} className="audit-report__scorebar-row">
                      <span className="audit-report__scorebar-label">
                        {c.category}
                      </span>

                      <div className="audit-report__scorebar-track">
                        <div
                          className="audit-report__scorebar-fill"
                          style={{
                            width: `${Math.max(0, Math.min(100, c.score))}%`,
                            background: scoreBarColor(c.score),
                          }}
                        />
                      </div>

                      <span
                        className="audit-report__scorebar-value"
                        style={{ color: scoreBarColor(c.score) }}
                      >
                        {c.score}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Key Metrics */}
            {info.keyMetrics.length > 0 && (
              <div className="audit-report__glance-card">
                <h3 className="audit-report__glance-title">Key Metrics</h3>

                <div className="audit-report__keymetrics">
                  {info.keyMetrics.map((m, i) => (
                    <div
                      key={i}
                      className={`audit-report__keymetric audit-report__keymetric--${m.sentiment}`}
                    >
                      <span className="audit-report__keymetric-value">
                        {formatMetricValue(m.value, m.unit)}
                      </span>
                      <span className="audit-report__keymetric-label">
                        {m.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Benchmarks vs. Recommended */}
            {info.benchmarks.length > 0 && (
              <div className="audit-report__glance-card">
                <h3 className="audit-report__glance-title">
                  Benchmarks vs. Recommended
                </h3>
                <p className="audit-report__glance-subtitle">
                  Each metric measured against its industry-standard target.
                </p>

                <div className="audit-report__bench-legend">
                  <span className="audit-report__bench-legend-item">
                    <span className="audit-report__bench-dot audit-report__bench-dot--good" />
                    meets target
                  </span>
                  <span className="audit-report__bench-legend-item">
                    <span className="audit-report__bench-dot audit-report__bench-dot--warning" />
                    borderline
                  </span>
                  <span className="audit-report__bench-legend-item">
                    <span className="audit-report__bench-dot audit-report__bench-dot--critical" />
                    off-target
                  </span>
                </div>

                <ul className="audit-report__benchmarks">
                  {info.benchmarks.map((b, i) => (
                    <li key={i} className="audit-report__benchmark-row">
                      <span
                        className={`audit-report__bench-dot audit-report__bench-dot--${b.status}`}
                      />
                      <span className="audit-report__benchmark-label">
                        {b.label}
                      </span>
                      <span
                        className={`audit-report__benchmark-value audit-report__benchmark-value--${b.status}`}
                      >
                        {b.value}
                      </span>
                      <span className="audit-report__benchmark-ideal">
                        target: {b.ideal}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Site Health Checks */}
            {info.healthChecks.length > 0 && (
              <div className="audit-report__glance-card">
                <h3 className="audit-report__glance-title">Site Health Checks</h3>

                <ul className="audit-report__healthchecks">
                  {info.healthChecks.map((h, i) => (
                    <li key={i} className="audit-report__healthcheck-row">
                      <span
                        className={`audit-report__health-dot ${
                          h.passed
                            ? "audit-report__health-dot--pass"
                            : "audit-report__health-dot--fail"
                        }`}
                      />
                      <span className="audit-report__healthcheck-label">
                        {h.label}
                      </span>
                      <span className="audit-report__healthcheck-detail">
                        {h.detail}
                      </span>
                      <span
                        className={`audit-report__healthcheck-status ${
                          h.passed ? "pass" : "fail"
                        }`}
                      >
                        {h.passed ? "PASS" : "FAIL"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations by Priority + Findings Overview */}
            <div className="audit-report__glance-row">
              {priorityTotal > 0 && (
                <div className="audit-report__glance-card audit-report__priority">
                  <h3 className="audit-report__glance-title">
                    Recommendations by Priority
                  </h3>

                  <div className="audit-report__priority-body">
                    <div className="audit-report__donut">
                      <svg viewBox="0 0 140 140">
                        <circle
                          className="audit-report__donut-track"
                          cx="70"
                          cy="70"
                          r={DONUT_R}
                        />
                        {(() => {
                          let offset = 0;
                          return prioritySegments.map((seg, i) => {
                            const len =
                              priorityTotal > 0
                                ? (seg.value / priorityTotal) * DONUT_C
                                : 0;
                            const dashOffset = -offset;
                            offset += len;
                            return (
                              <circle
                                key={i}
                                className="audit-report__donut-seg"
                                cx="70"
                                cy="70"
                                r={DONUT_R}
                                stroke={seg.color}
                                strokeDasharray={`${len} ${DONUT_C - len}`}
                                strokeDashoffset={dashOffset}
                              />
                            );
                          });
                        })()}
                      </svg>

                      <div className="audit-report__donut-center">
                        <span className="audit-report__donut-total">
                          {priorityTotal}
                        </span>
                        <span className="audit-report__donut-caption">TOTAL</span>
                      </div>
                    </div>

                    <ul className="audit-report__priority-legend">
                      {prioritySegments.map((seg, i) => (
                        <li key={i}>
                          <span
                            className="audit-report__legend-dot"
                            style={{ background: seg.color }}
                          />
                          <span className="audit-report__legend-label">
                            {seg.label}
                          </span>
                          <span className="audit-report__legend-value">
                            {seg.value}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {findingsTotal > 0 && (
                <div className="audit-report__glance-card audit-report__findings-overview">
                  <h3 className="audit-report__glance-title">Findings Overview</h3>

                  <div className="audit-report__findings-bar">
                    <div
                      className="audit-report__findings-strengths"
                      style={{
                        width: `${
                          (info.findingsSummary.strengths / findingsTotal) * 100
                        }%`,
                      }}
                    />
                    <div
                      className="audit-report__findings-weaknesses"
                      style={{
                        width: `${
                          (info.findingsSummary.weaknesses / findingsTotal) * 100
                        }%`,
                      }}
                    />
                  </div>

                  <div className="audit-report__findings-legend">
                    <span className="audit-report__findings-legend--strengths">
                      Strengths: {info.findingsSummary.strengths}
                    </span>
                    <span className="audit-report__findings-legend--weaknesses">
                      Weaknesses: {info.findingsSummary.weaknesses}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ================= SNAPSHOT ================= */}
       {report?.snapshot && (
  <section className="audit-report__snapshot">
    <h2 className="audit-report__section-heading">Website Snapshot</h2>

    <div className="audit-report__snapshot-card">
      <img
        src={report.snapshot.image}
        alt={report.snapshot.title ?? "snapshot"}
      />

      <div>
        <h3>{report.snapshot.title}</h3>
        <p>{report.snapshot.description}</p>
        <small>{report.snapshot.metaDescription}</small>
      </div>
    </div>
  </section>
)}

       {/* ================= DEEP DIVE ================= */}
<section className="audit-report__deepdive">
  <h2 className="audit-report__section-heading">Deep Dive Analysis</h2>

  <div className="audit-report__cards">
    {(report?.deepDive ?? []).map((item, index) => (
      <article key={item.key || index} className="audit-report__card">

       <div className="audit-report__card-head">
  <div className="audit-report__card-title-wrap">
    <span className="audit-report__card-icon">
  {deepDiveIcons[item.icon] ?? deepDiveIcons.default}
</span>

    <h3 className="audit-report__card-title">
      {item.title}
    </h3>
  </div>

  <span className="audit-report__card-score">
    <span className="audit-report__card-score-num">
      {item.score}/100
    </span>
<span className="audit-report__card-score-label">SCORE</span>
  </span>
</div>
        {item.subtitle && <p className="audit-report__card-subtitle">{item.subtitle}</p>}

       {/* Issues Block */}
{item.findings && item.findings.length > 0 && (
  <div className="audit-report__card-block">
  

    <div className="audit-report__findings">
      {item.findings.map((finding, fIndex) => (
        <div key={fIndex} className="audit-report__finding">

          <div className="audit-report__finding-row">
            <span className="audit-report__finding-label">Title:</span>
            <span className="audit-report__finding-value">
              {finding.title}
            </span>
          </div>

          <div className="audit-report__finding-row">
            <span className="audit-report__finding-label">Impact:</span>
            <span className="audit-report__finding-value">
              {finding.impact}
            </span>
          </div>

          {finding.evidence && (
            <div className="audit-report__finding-row">
              <span className="audit-report__finding-label">Evidence:</span>
              <span className="audit-report__finding-value">
                {finding.evidence}
              </span>
            </div>
          )}

        </div>
      ))}
    </div>
  </div>
)}
        {/* Assessment Block */}
        <div className="audit-report__card-block">
          <span className="audit-report__block-label">Assessment</span>
          <p className="audit-report__block-text">{item.assessment}</p>
        </div>

        {/* Recommendations Block */}
        {item.recommendations && item.recommendations.length > 0 && (
          <div className="audit-report__card-block">
            <span className="audit-report__block-label">Recommendations</span>
            <ul className="audit-report__block-list">
              {item.recommendations.map((rec, rIndex) => (
                <li key={rIndex}>
                  <strong>[{rec.priority}]</strong> {rec.action}
                  <span className="audit-report__recommendation-impact">
                    ({rec.impact})
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </article>
    ))}
  </div>
</section>

        {/* ================= QUICK WINS ================= */}
        <section className="audit-report__quickwins">
          <h2>{report.quickWins.title}</h2>
          <p>{report.quickWins.subtitle}</p>

          <div className="audit-report__wins-grid">
            {(report?.quickWins?.items ?? []).map((win, i) => (
              <div key={i} className="audit-report__win">
                <h3>{win.title}</h3>
                <p>{win.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ================= ACTION PLAN ================= */}
        <section className="audit-report__actionplan">
          <h2 className="audit-report__section-heading">90-Day Action Plan</h2>

          <div className="audit-report__action-grid">
            <div className="audit-report__action-column">
              <h3>Next 30 Days</h3>
              <ul>
                {(report?.actionPlan?.next30Days ?? []).map((item, idx) => (
                  <li key={`30-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="audit-report__action-column">
              <h3>Next 60 Days</h3>
              <ul>
                {(report?.actionPlan?.next60Days ?? []).map((item, idx) => (
                  <li key={`60-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="audit-report__action-column">
              <h3>Next 90 Days</h3>
              <ul>
                {(report?.actionPlan?.next90Days ?? []).map((item, idx) => (
                  <li key={`90-${idx}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ================= METRICS TO TRACK ================= */}
        <section>
          <h2 className="audit-report__section-heading">Metrics To Track</h2>

          <div className="audit-report__metrics-grid">
            {(report?.metricsToTrack ?? []).map((m, i) => (
              <div key={i} className="audit-report__metric-card">
                <h3>{m.metric}</h3>
                <p>{m.why}</p>
                <span className="metric-target">{m.target}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ================= COMPETITOR LENS ================= */}
        <section>
          <h2 className="audit-report__section-heading">Competitor Lens</h2>
          <div className="audit-report__competitor-box">
            {report.competitorLens}
          </div>
        </section>

        {/* ================= INDUSTRY TRENDS ================= */}
        {report?.industryTrends &&
          (report.industryTrends.threats.length > 0 ||
            report.industryTrends.opportunities.length > 0 ||
            report.industryTrends.trends.length > 0) && (
            <section className="audit-report__trends">
              <h2 className="audit-report__section-heading">Industry Trends</h2>

              <div className="audit-report__cards">
                <div className="audit-report__card">
                  <div className="audit-report__card-head">
                    <div className="audit-report__card-title-wrap">
                      <span className="audit-report__card-icon">
                        {IndustryIcons["Threats"]}
                      </span>
                      <h3 className="audit-report__card-title">Threats</h3>
                    </div>
                  </div>
                  <ul>
                    {(report.industryTrends.threats ?? []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>

                <div className="audit-report__card">
                  <div className="audit-report__card-head">
                    <div className="audit-report__card-title-wrap">
                      <span className="audit-report__card-icon">
                        {IndustryIcons["Opportunities"]}
                      </span>
                      <h3 className="audit-report__card-title">Opportunities</h3>
                    </div>
                  </div>
                  <ul>
                    {(report.industryTrends.opportunities ?? []).map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                </div>

                <div className="audit-report__card">
                  <div className="audit-report__card-head">
                    <div className="audit-report__card-title-wrap">
                      <span className="audit-report__card-icon">
                        {IndustryIcons["Trends"]}
                      </span>
                      <h3 className="audit-report__card-title">Trends</h3>
                    </div>
                  </div>
                  <ul>
                    {(report.industryTrends.trends ?? []).map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>
          )}

        {/* ================= PAIN POINT RESPONSE ================= */}
        <section className="audit-report__painpoints">
          <h2 className="audit-report__painpoints-title">
            {report.painPoints.title}
          </h2>
          <p className="audit-report__painpoints-body">
            {report.painPoints.body}
          </p>

          {report.painPoints.userSubmitted && (
            <p className="audit-report__painpoints-quote">
              “{report.painPoints.userSubmitted}”
            </p>
          )}
        </section>

        {/* ================= CTA ================= */}
        <section className="audit-report__cta">
  <h2>Download Your Audit Report</h2>

  <button
    className="audit-report__cta-btn"
    onClick={handleDownloadReport}
    disabled={isDownloading}
  >
    <DownloadIcon size={28} />
    {isDownloading ? "Sending..." : report.cta.buttonText}
  </button>

{downloadMessage && (
  <p
    className={`audit-report__cta-message ${
      downloadMessage.toLowerCase().includes("success") ||
      downloadMessage.toLowerCase().includes("sent")
        ? "success"
        : "error"
    }`}
  >
    {downloadMessage}
  </p>
)}
</section>
      </div>
    </div>
  );
};

export default AuditReport;